import { PROVIDER_API, USE_MOCK_AUTH } from "../config/api";
import { displayPersonName } from "../utils/personName";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

const LIST_TTL_MS = 60_000;
const LIST_PAGE_LIMIT = 100;
const PAYMENTS_PAGE_LIMIT = 500;
const listCache = new Map();

function extractRows(data, key) {
  const raw = data?.[key];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

function runListDeduped(key, factory, { force = false, ttlMs = LIST_TTL_MS } = {}) {
  const hit = listCache.get(key);
  if (!force && hit?.result != null && Date.now() - hit.at < ttlMs) {
    return Promise.resolve(hit.result);
  }
  if (!force && hit?.promise) return hit.promise;

  const promise = Promise.resolve()
    .then(factory)
    .then((result) => {
      listCache.set(key, { at: Date.now(), result, promise: null });
      return result;
    })
    .catch((err) => {
      const current = listCache.get(key);
      if (current?.promise === promise) listCache.delete(key);
      throw err;
    });

  listCache.set(key, { at: hit?.at || 0, result: hit?.result ?? null, promise });
  return promise;
}

async function fetchAllPages(token, path, key, extra = {}) {
  const rows = [];
  let page = 1;
  let lastPage = 1;

  do {
    const params = new URLSearchParams({ page: String(page), limit: String(LIST_PAGE_LIMIT) });
    Object.entries(extra).forEach(([name, value]) => {
      if (value !== undefined && value !== null && value !== "") params.set(name, String(value));
    });
    const json = await apiRequest(`${path}?${params}`, { method: "GET", token });
    const data = unwrapApiEnvelope(json);
    rows.push(...extractRows(data, key));
    lastPage = Number(data?.pagination?.last_page || 1);
    page += 1;
  } while (page <= lastPage && page <= 40);

  return rows;
}

function normalizePaymentType(value) {
  const key = String(value || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (!key) return "—";
  if (
    key === "mtn" ||
    key === "mtnmomo" ||
    key === "telecel" ||
    key === "telecelcash" ||
    key === "vodafone" ||
    key === "airteltigo" ||
    key === "airtel" ||
    key === "at"
  ) {
    return "momo";
  }
  return value;
}

export function normalizePayment(raw) {
  if (!raw) return null;
  const owed = Number(raw.outstanding ?? raw.outstanding_balance ?? raw.amount_owed ?? raw.amountOwed ?? 0);
  const paid = Number(raw.amount_paid ?? raw.amountPaid ?? raw.amount ?? 0);
  return {
    id: raw.reference_id || raw.payment_id || String(raw.id ?? ""),
    numericId: raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null,
    payment_id: raw.reference_id || raw.payment_id || null,
    first_name: raw.first_name || "",
    last_name: raw.last_name || "",
    fullname: raw.fullname || "",
    customer_name: raw.customer_name || "",
    customer: displayPersonName(
      { first_name: raw.first_name, last_name: raw.last_name },
      raw.fullname,
      raw.customer_name,
      raw.customer
    ),
    caseId: raw.case_code || raw.case_id || raw.caseId || "—",
    amount: Number(raw.amount_paid ?? raw.amount ?? 0),
    amountOwed: owed,
    amountPaid: paid,
    outstandingAmount: Number(raw.outstanding ?? raw.outstandingAmount ?? Math.max(0, owed - paid)),
    paymentType: normalizePaymentType(raw.payment_method || raw.payment_type || raw.paymentType),
    method: raw.payment_method || raw.interaction_method || raw.method || "—",
    status: raw.status || "pending",
    reconciled: Boolean(raw.reconciled),
    comment: raw.comment || "",
    commentPresets: raw.comment_presets || raw.commentPresets || [],
    receivedAt: raw.updated_at || raw.created_at || raw.receivedAt,
    reference: raw.reference_id || raw.reference || "—",
    referenceId: raw.reference_id || raw.referenceId || null,
    msisdn: raw.msisdn || "",
    network: raw.network || "",
    providerStatus: raw.provider_status || raw.providerStatus || "",
    source: raw.source || "manual",
  };
}

export function formatCallDuration(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "string" && /hour|minute/i.test(value)) return value;
  const total = Number(value);
  if (!Number.isFinite(total)) return String(value);
  const minutes = Math.max(0, Math.round(total));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (mins > 0 || hours === 0) parts.push(`${mins} ${mins === 1 ? "minute" : "minutes"}`);
  return parts.join(" ");
}

export function normalizeCallLog(raw) {
  if (!raw) return null;
  const promised = Number(raw.promised_amount ?? raw.promisedAmount ?? 0);
  const numericId = raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null;
  return {
    id: numericId != null ? String(numericId) : String(raw.call_log_id ?? ""),
    numericId,
    customer: displayPersonName(
      { first_name: raw.first_name, last_name: raw.last_name },
      raw.fullname,
      raw.customer_name,
      raw.customer
    ),
    caseId: raw.case_code || raw.case_id || raw.caseId || "—",
    agent: raw.agent || raw.engagement_by || "—",
    type: raw.call_type || raw.interaction_method || raw.type || "Call",
    duration: formatCallDuration(raw.duration_in_minutes ?? raw.duration),
    outcome: raw.outcome || raw.summary || "Logged",
    loggedAt: raw.engagement_at || raw.logged_at || raw.created_at,
    promisedAmount: promised,
    dueDate: raw.due_date || raw.dueDate,
    recordedBy: raw.recorded_by || raw.agent || "—",
    followUp: Boolean(raw.follow_up ?? raw.followUp),
    summary: raw.summary || "",
  };
}

export function normalizeEscalation(raw) {
  if (!raw) return null;
  return {
    id: raw.escalation_id || raw.id,
    caseId: raw.case_id || raw.caseId,
    customer: raw.fullname || raw.customer_name || raw.customer,
    reason: raw.reason,
    path: raw.path,
    legalStatus: raw.status_label || raw.status || raw.legalStatus || "submitted",
    assignedCounsel: raw.assigned_to_name || raw.submitted_by_name || raw.assignedCounsel || "—",
    escalatedAt: raw.created_at ? String(raw.created_at).slice(0, 10) : raw.escalatedAt,
    amountOwed: Number(raw.outstanding_balance ?? raw.amount_owed ?? raw.amountOwed ?? 0),
    amountPaid: Number(raw.amount_paid ?? raw.amountPaid ?? 0),
    urgency: raw.urgency,
  };
}

export async function fetchProviderDashboardOverview(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;
  return runListDeduped(
    `overview:${token}`,
    async () => {
      const json = await apiRequest(PROVIDER_API.dashboardOverview, { method: "GET", token });
      return unwrapApiEnvelope(json);
    },
    { force }
  );
}

export async function fetchProviderDashboardReport(token, period = "30D", { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;
  return runListDeduped(
    `charts:${token}:${period}`,
    async () => {
      const params = new URLSearchParams({ period: String(period || "30D") });
      const json = await apiRequest(`${PROVIDER_API.dashboardCharts}?${params}`, {
        method: "GET",
        token,
      });
      return unwrapApiEnvelope(json);
    },
    { force }
  );
}

export async function fetchProviderDashboardCharts(token, period = "30D", options = {}) {
  return fetchProviderDashboardReport(token, period, options);
}

export async function fetchProviderDashboardPriorityCases(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return [];
  return runListDeduped(
    `priority:${token}`,
    async () => {
      const json = await apiRequest(PROVIDER_API.dashboardPriorityCases, {
        method: "GET",
        token,
      });
      const data = unwrapApiEnvelope(json);
      return Array.isArray(data?.priority_cases) ? data.priority_cases : [];
    },
    { force }
  );
}

export async function fetchProviderPayments(token, extra = {}, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return [];
  return runListDeduped(
    `payments:${token}:${JSON.stringify(extra)}`,
    async () => {
      const rows = await fetchAllPages(token, PROVIDER_API.payments, "payments", {
        ...extra,
        limit: PAYMENTS_PAGE_LIMIT,
      });
      return rows.map(normalizePayment).filter(Boolean);
    },
    { force }
  );
}

export async function fetchProviderPaymentsSummary(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;
  return runListDeduped(
    `payments-summary:${token}`,
    async () => {
      const json = await apiRequest(PROVIDER_API.paymentsSummary, { method: "GET", token });
      return unwrapApiEnvelope(json);
    },
    { force }
  );
}

export async function fetchProviderCallLogs(token, extra = {}) {
  if (USE_MOCK_AUTH || !token) return [];
  const rows = await fetchAllPages(token, PROVIDER_API.callLogs, "call_logs", extra);
  return rows.map(normalizeCallLog).filter(Boolean);
}

export async function fetchProviderCallLogsSummary(token) {
  if (USE_MOCK_AUTH || !token) return null;
  const json = await apiRequest(PROVIDER_API.callLogsSummary, { method: "GET", token });
  const raw = unwrapApiEnvelope(json);
  if (!raw || typeof raw !== "object") return null;

  return {
    calls_logged: Number(raw.calls_logged ?? raw.total_logs ?? 0),
    promises_recorded: Number(raw.promises_recorded ?? 0),
    active_payment_plans: Number(raw.active_payment_plans ?? 0),
    avg_handle_time: Number(raw.avg_handle_time ?? 0),
    // Legacy aliases kept for older UI bindings
    total_logs: Number(raw.calls_logged ?? raw.total_logs ?? 0),
    follow_ups: Number(raw.active_payment_plans ?? raw.follow_ups ?? 0),
    today: Number(raw.today ?? 0),
  };
}

export async function fetchProviderEscalations(token, extra = {}) {
  if (USE_MOCK_AUTH || !token) return [];
  const rows = await fetchAllPages(token, PROVIDER_API.escalations, "escalations", extra);
  return rows.map(normalizeEscalation).filter(Boolean);
}
